// Google Maps wrapper with marker clustering for 20k+ pins.
// Uses @vis.gl/react-google-maps for the map and @googlemaps/markerclusterer
// for efficient rendering at low zoom levels.

import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { APIProvider, Map, useMap, InfoWindow } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import type { AutoZoomMode, GeoJsonFeature } from '../lib/api';

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;

// Default center: continental US
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 5;

// Accounts at the same address geocode to identical lat/lng, so their markers
// stack pixel-perfectly and only the top one is visible/clickable. Fan each
// co-located group out on a tiny phyllotaxis (sunflower) spread so every pin is
// individually selectable when zoomed in. The first member stays on the true
// point; the rest spiral outward a few meters. Returns a per-feature-id map of
// display coordinates, used for both marker positions and popup anchors.
const SPREAD_RADIUS_DEG = 0.0001; // ~11m base step
const GOLDEN_ANGLE = 2.399963; // 137.5° in radians

// Keyed by feature id. (`Map` the identifier is the imported map component in
// this file, so use a plain record rather than a JS Map.)
type DisplayPositions = Record<string, { lat: number; lng: number }>;

function computeDisplayPositions(features: GeoJsonFeature[]): DisplayPositions {
  const groups: Record<string, GeoJsonFeature[]> = {};
  for (const f of features) {
    const [lng, lat] = f.geometry.coordinates;
    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    (groups[key] ??= []).push(f);
  }

  const out: DisplayPositions = {};
  for (const group of Object.values(groups)) {
    const [lng0, lat0] = group[0].geometry.coordinates;
    if (group.length === 1) {
      out[group[0].properties.id] = { lat: lat0, lng: lng0 };
      continue;
    }
    // Longitude degrees shrink with latitude — divide out cos(lat) so the
    // spread stays visually circular rather than squashed.
    const cosLat = Math.max(Math.cos((lat0 * Math.PI) / 180), 0.1);
    group.forEach((f, k) => {
      const radius = SPREAD_RADIUS_DEG * Math.sqrt(k);
      const angle = k * GOLDEN_ANGLE;
      out[f.properties.id] = {
        lat: lat0 + radius * Math.sin(angle),
        lng: lng0 + (radius * Math.cos(angle)) / cosLat,
      };
    });
  }
  return out;
}

interface OpenPopup {
  feature: GeoJsonFeature;
  content: ReactNode;
}

interface MapViewProps {
  features: GeoJsonFeature[];
  onMarkerClick: (feature: GeoJsonFeature) => void;
  popups?: OpenPopup[];
  onPopupClose?: (featureId: string) => void;
  autoZoom: AutoZoomMode;
  selectedViewId: number | null;
  clusterMarkers: boolean;
  /** A radius filter in force: drawn as a circle and included when the view is fitted. */
  ring?: { lat: number; lng: number; radiusMi: number } | null;
  /** 'hybrid' for the satellite view a CRM link asks for; the map's own control can switch afterwards. */
  mapTypeId?: 'roadmap' | 'satellite' | 'hybrid';
}

const MI_TO_M = 1609.344;

/** The radius rule as a circle on the map. */
function RingOverlay({ ring }: { ring: MapViewProps['ring'] }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !ring) return;
    const circle = new google.maps.Circle({
      map,
      center: { lat: ring.lat, lng: ring.lng },
      radius: ring.radiusMi * MI_TO_M,
      strokeColor: '#ffffff',
      strokeOpacity: 0.85,
      strokeWeight: 2,
      fillColor: '#b23b1e',
      fillOpacity: 0.12,
      clickable: false,
    });
    return () => circle.setMap(null);
  }, [map, ring]);
  return null;
}

function MapMarkers({
  features,
  displayPositions,
  onMarkerClick,
  autoZoom,
  selectedViewId,
  clusterMarkers,
  ring,
}: Pick<
  MapViewProps,
  'features' | 'onMarkerClick' | 'autoZoom' | 'selectedViewId' | 'clusterMarkers' | 'ring'
> & { displayPositions: DisplayPositions }) {
  const map = useMap();
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const lastZoomedViewIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!map) return;

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current.setMap(null);
      clustererRef.current = null;
    }
    for (const m of markersRef.current) m.map = null;
    markersRef.current = [];

    if (features.length === 0) return;

    const viewChanged = lastZoomedViewIdRef.current !== selectedViewId;
    const shouldZoom = autoZoom === 'on-features-change' || viewChanged;

    if (shouldZoom) {
      const bounds = new google.maps.LatLngBounds();
      for (const f of features) {
        const [lng, lat] = f.geometry.coordinates;
        bounds.extend(displayPositions[f.properties.id] ?? { lat, lng });
      }
      if (ring) {
        // Frame the whole ring, as the CRM's satellite picture does.
        const r = new google.maps.Circle({ center: { lat: ring.lat, lng: ring.lng }, radius: ring.radiusMi * MI_TO_M });
        const rb = r.getBounds();
        if (rb) bounds.union(rb);
      }
      map.fitBounds(bounds, 50);
      lastZoomedViewIdRef.current = selectedViewId;
    }

    // Build markers in batches across animation frames so a 30k-view doesn't freeze the tab.
    // Each AdvancedMarkerElement is a DOM element — synchronous construction at that scale stalls.
    const BATCH_SIZE = 500;
    let cancelled = false;
    let rafId: number | null = null;
    const markers: google.maps.marker.AdvancedMarkerElement[] = [];
    let cursor = 0;

    function processBatch() {
      if (cancelled) return;
      const end = Math.min(cursor + BATCH_SIZE, features.length);
      for (let i = cursor; i < end; i++) {
        const feature = features[i];
        const [lng, lat] = feature.geometry.coordinates;
        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: displayPositions[feature.properties.id] ?? { lat, lng },
          title: feature.properties.name,
        });
        marker.addListener('click', () => onMarkerClick(feature));
        markers.push(marker);
      }
      cursor = end;

      if (cursor < features.length) {
        rafId = requestAnimationFrame(processBatch);
        return;
      }

      if (cancelled) return;
      markersRef.current = markers;
      if (clusterMarkers) {
        clustererRef.current = new MarkerClusterer({
          map,
          markers,
          onClusterClick:
            autoZoom === 'on-view-change' ? () => undefined : undefined,
        });
      } else {
        // No clustering — attach each marker directly to the map.
        for (const m of markers) m.map = map;
      }
    }

    rafId = requestAnimationFrame(processBatch);

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }
      for (const m of markersRef.current) m.map = null;
    };
  }, [map, features, displayPositions, onMarkerClick, clusterMarkers, ring]);

  return null;
}

export default function MapView({
  features,
  onMarkerClick,
  popups,
  onPopupClose,
  autoZoom,
  selectedViewId,
  clusterMarkers,
  ring,
  mapTypeId,
}: MapViewProps) {
  // Shared between marker placement and popup anchoring so a popup opens over
  // its (possibly spread-out) pin rather than the raw stacked coordinate.
  const displayPositions = useMemo(() => computeDisplayPositions(features), [features]);
  return (
    <APIProvider apiKey={API_KEY} libraries={['marker']}>
      <Map
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        mapId={MAP_ID}
        mapTypeId={mapTypeId}
        gestureHandling="greedy"
        disableDefaultUI={false}
        className="h-full w-full"
      >
        <MapMarkers
          features={features}
          displayPositions={displayPositions}
          onMarkerClick={onMarkerClick}
          autoZoom={autoZoom}
          selectedViewId={selectedViewId}
          clusterMarkers={clusterMarkers}
          ring={ring}
        />
        <RingOverlay ring={ring} />
        {(popups ?? []).map(({ feature, content }) => (
          <InfoWindow
            key={feature.properties.id}
            position={
              displayPositions[feature.properties.id] ?? {
                lat: feature.geometry.coordinates[1],
                lng: feature.geometry.coordinates[0],
              }
            }
            onCloseClick={() => onPopupClose?.(feature.properties.id)}
            pixelOffset={[0, -32]}
            disableAutoPan={autoZoom === 'on-view-change'}
            headerDisabled
          >
            {content}
          </InfoWindow>
        ))}
      </Map>
    </APIProvider>
  );
}
